/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { useAuth } from 'react-oidc-context';

/**
 * @typedef {Object} SendMessageState
 * @property {boolean} isLoadingSendMessage - Flag indicating if the message sending process is loading
 * @property {boolean} isFetchData - Flag indicating if data is being fetched
 * @property {Array<{id: string}>} areas - List of available areas
 * @property {Array<Object>} employees - List of available employees
 * @property {Object} kpis - Object containing KPIs information
 * @property {Array<{id: string, name: string}>} kpis.kpis - List of available KPIs
 * @property {number} kpis.minValue - Minimum value for KPI
 * @property {number} kpis.maxValue - Maximum value for KPI
 * @property {boolean} openSendMessage - Flag indicating if send message dialog is open
 * @property {Object|null} anchorEl - Anchor element for the popover
 * @property {Object} formSendMessageData - Form data for sending message
 * @property {string} formSendMessageData.type - Type of message delivery (area, employee, kpi)
 * @property {string|null} formSendMessageData.messageID - ID of the message to send
 * @property {Object} errorsFormSendMessage - Form validation errors
 * @property {boolean} openConfirmSendMessage - Flag indicating if confirmation dialog is open
 * @property {Object|null} anchorElConfirmSendMessage - Anchor element for confirmation popover
 * @property {boolean} showAlertSendMessage - Flag indicating if alert should be shown
 * @property {string} typeAlertSendMessage - Type of alert (success, error)
 * @property {string} alertMessageSendMessage - Alert message content
 */

/**
 * Custom hook for managing message sending functionality
 *
 * @returns {Object} Object containing state and functions for message sending
 */
export const useSendMessage = () => {
  const { t } = useTranslation();

  const auth = useAuth();
  const authId = auth.user?.profile.sub;

  const initialState = {
    isLoadingSendMessage: true,
    isFetchData: false,
    areas: [],
    employees: [],
    kpis: {
      kpis: [],
      minValue: 0,
      maxValue: 0,
    },
    openSendMessage: false,
    anchorEl: null,
    formSendMessageData: {
      type: '',
      messageID: null,
    },
    errorsFormSendMessage: {
      type: false,
      area: false,
      employee: false,
      kpi: false,
      kpiValue: false,
    },
    openConfirmSendMessage: false,
    anchorElConfirmSendMessage: null,
    showAlertSendMessage: false,
    typeAlertSendMessage: 'success',
    alertMessageSendMessage: '',
  };

  const [isLoadingSendMessage, setIsLoadingSendMessage] = useState(
    initialState.isLoadingSendMessage
  );

  const [showAlertSendMessage, setShowAlertSendMessage] = useState(
    initialState.showAlertSendMessage
  );
  const [alertMessageSendMessage, setAlertMessageSendMessage] = useState(
    initialState.alertMessageSendMessage
  );
  const [typeAlertSendMessage, setTypeAlertSendMessage] = useState(
    initialState.typeAlertSendMessage
  );

  const [isFetchData, setIsFetchData] = useState(initialState.isFetchData);

  const [areas, setAreas] = useState(initialState.areas);

  const [employees, setEmployees] = useState(initialState.employees);

  const [kpis, setKpis] = useState(initialState.kpis);

  const [openSendMessage, setOpenSendMessage] = useState(
    initialState.openSendMessage
  );
  const [openConfirmSendMessage, setOpenConfirmSendMessage] = useState(
    initialState.openConfirmSendMessage
  );
  const [anchorEl, setAnchorEl] = useState(initialState.anchorEl);
  const [formSendMessageData, setFormSendMessageData] = useState(
    initialState.formSendMessageData
  );

  const [errorsFormSendMessage, setErrorsFormSendMessage] = useState(
    initialState.errorsFormSendMessage
  );
  const [anchorElConfirmSendMessage, setAnchorElConfirmSendMessage] = useState(
    initialState.anchorElConfirmSendMessage
  );

  useEffect(() => {
    const fetchData = async () => {
      const areas = await fetchAreas();
      setAreas(areas);

      const employees = await fetchEmployees();
      setEmployees(employees);

      const kpis = await fetchKPIs();
      setKpis(kpis);
      setIsFetchData(true);
    };

    if (openSendMessage) {
      setIsLoadingSendMessage(true);
      fetchData();
      return;
    }

    setIsLoadingSendMessage(false);
  }, [openSendMessage]);

  useEffect(() => {
    if (isFetchData) {
      setIsLoadingSendMessage(false);
      setIsFetchData(initialState.isFetchData);
    }
  }, [isFetchData]);

  /**
   * Closes the alert message and resets its content
   */
  const handleCloseAlertSendMessage = () => {
    setShowAlertSendMessage(false);
    setAlertMessageSendMessage(initialState.alertMessageSendMessage);
  };

  /**
   * Fetches available areas from the API and sorts them alphabetically
   *
   * @async
   * @returns {Promise<Array<{id: string}>>} Array of area objects with id property
   */
  const fetchAreas = async () => {
    try {
      const { data } = await api.get('user/areas');

      const { areas } = data;

      if (areas.length <= 0) {
        return [];
      }

      const sortedAreas = [...areas].sort((a, b) => a.localeCompare(b));

      const areasWithId = sortedAreas.map((area) => ({
        id: area,
      }));

      return areasWithId;
    } catch (error) {
      console.error(`fetchAreas: ${error.message}`);
      return [];
    }
  };

  /**
   * Fetches employees from the API and sorts them alphabetically by name
   *
   * @async
   * @returns {Promise<Array<Object>>} Array of employee objects
   */
  const fetchEmployees = async () => {
    try {
      const { data: employees } = await api.get(
        'filter-users?orderBy=name&orderDir=asc'
      );

      const sortedEmployees = [...employees].sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      return sortedEmployees;
    } catch (error) {
      console.error(`fetchEmployees: ${error.message}`);
      return [];
    }
  };

  /**
   * Fetches KPIs from the API and sorts them alphabetically by name
   *
   * @async
   * @returns {Promise<Object>} Object containing KPIs information with sorted kpis array
   */
  const fetchKPIs = async () => {
    try {
      const { data: kpis } = await api.get('user/kpis');

      const sortedKpis = {
        ...kpis,
        kpis: [...kpis.kpis].sort((a, b) => a.name.localeCompare(b.name)),
      };

      return sortedKpis;
    } catch (error) {
      console.error(`fetchKPIs: ${error.message}`);
      return [];
    }
  };

  /**
   * Sends a message based on the provided message data
   *
   * @async
   * @param {Object} messageData - Data for the message to be sent
   * @param {string} messageData.type - Type of message delivery (area, employee, kpi)
   * @param {string} messageData.messageID - ID of the message to send
   * @param {string} [messageData.areaID] - ID of the area (required if type is 'area')
   * @param {Object} [messageData.employee] - Employee object (required if type is 'employee')
   * @param {Object} [messageData.kpi] - KPI object (required if type is 'kpi')
   * @param {number} [messageData.kpiValue] - KPI value (required if type is 'kpi')
   * @returns {Promise<void>}
   */
  const sendMessage = async (messageData) => {
    if (messageData.type === 'area') {
      const { messageID, areaID } = messageData;
      await api.post('send-messages', {
        messageId: messageID,
        dispatchType: 'area',
        area: areaID,
        createdBy: authId,
      });
      return;
    }

    if (messageData.type === 'employee') {
      const { messageID, employee } = messageData;
      await api.post('send-messages', {
        messageId: messageID,
        dispatchType: 'user',
        externalIds: [employee.external_id],
        createdBy: authId,
      });
      return;
    }

    if (messageData.type === 'kpi') {
      const { messageID, kpiValue, kpi } = messageData;
      await api.post('send-messages', {
        messageId: messageID,
        dispatchType: 'criteria',
        criteria: kpi.id,
        criteriaValue: kpiValue,
        createdBy: authId,
      });
      return;
    }
  };

  /**
   * Handles the message sending process, including error handling and UI updates
   *
   * @async
   * @param {Object} messageData - Data for the message to be sent
   * @returns {Promise<void>}
   */
  const handleSendMessage = async (messageData) => {
    try {
      handleCloseConfirmSendMessage();
      setIsLoadingSendMessage(true);
      await sendMessage(messageData);
      setTypeAlertSendMessage('success');
      setAlertMessageSendMessage(t('send message.message sent successfully'));
      setShowAlertSendMessage(true);
      handleCloseSendMessage();
    } catch (error) {
      console.error(`handleSendMessage ${error.message}`);
      setTypeAlertSendMessage('error');
      setAlertMessageSendMessage(t('send message.failed to send message'));
      setShowAlertSendMessage(true);
      handleCloseConfirmSendMessage();
      handleCloseSendMessage();
    }
  };

  /**
   * Opens the send message dialog
   *
   * @param {React.SyntheticEvent} event - The event that triggered the dialog opening
   * @param {Object} selectedMessage - The message selected to be sent
   * @param {string} selectedMessage.id - ID of the selected message
   */
  const handleOpenSendMessage = (event, selectedMessage) => {
    setAnchorEl(event.currentTarget);
    setFormSendMessageData({
      ...initialState.formSendMessageData,
      messageID: selectedMessage.id,
    });
    setOpenSendMessage(true);
  };

  /**
   * Closes the send message dialog and resets form data
   */
  const handleCloseSendMessage = () => {
    setAnchorEl(null);
    setOpenSendMessage(false);
    setFormSendMessageData(initialState.formSendMessageData);
    setErrorsFormSendMessage(initialState.errorsFormSendMessage);
  };

  /**
   * Opens the confirmation dialog for sending a message after validating the form
   *
   * @param {React.SyntheticEvent} event - The event that triggered the dialog opening
   */
  const handleOpenConfirmSendMessage = (event) => {
    const isValid = validFormSendMessage();

    if (isValid) {
      setAnchorElConfirmSendMessage(event.currentTarget);
      setOpenConfirmSendMessage(true);
      return;
    }
  };

  /**
   * Closes the confirmation dialog for sending a message
   */
  const handleCloseConfirmSendMessage = () => {
    setOpenConfirmSendMessage(false);
  };

  /**
   * Updates the message type and resets form errors
   *
   * @param {string} type - The type of message delivery (area, employee, kpi)
   * @param {Object} selectedMessage - The message selected to be sent
   * @param {string} selectedMessage.id - ID of the selected message
   */
  const updateTypeSend = (type, selectedMessage) => {
    setErrorsFormSendMessage(initialState.errorsFormSendMessage);
    setFormSendMessageData({
      ...initialState.formSendMessageData,
      type: type,
      messageID: selectedMessage.id,
    });
  };

  /**
   * Updates a specific field in the form data
   *
   * @param {string} field - The field name to update
   * @param {*} value - The new value for the field
   */
  const updateFormSendMessageData = (field, value) => {
    setFormSendMessageData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Validates the form data for sending a message
   *
   * @returns {boolean} True if the form is valid, false otherwise
   */
  const validFormSendMessage = () => {
    const errors = initialState.errorsFormSendMessage;

    const { type } = formSendMessageData;

    if (!type) {
      errors.type = true;
      setErrorsFormSendMessage(errors);
      setTypeAlertSendMessage('error');
      setAlertMessageSendMessage(t('send message.select a delivery type'));
      setShowAlertSendMessage(true);
      return false;
    }

    if (type === 'area' && !formSendMessageData.areaID) {
      errors.area = true;
      setErrorsFormSendMessage(errors);
      setTypeAlertSendMessage('error');
      setAlertMessageSendMessage(t('send message.select an area'));
      setShowAlertSendMessage(true);
      return false;
    }

    if (type === 'employee' && !formSendMessageData.employee?.id) {
      errors.employee = true;
      setErrorsFormSendMessage(errors);
      setTypeAlertSendMessage('error');
      setAlertMessageSendMessage(t('send message.select an employee'));
      setShowAlertSendMessage(true);
      return false;
    }

    if (type === 'kpi') {
      const hasKpi = !!formSendMessageData.kpi?.id;
      const hasKpiValue = !!formSendMessageData.kpiValue;

      if (!hasKpiValue && !hasKpi) {
        errors.kpi = true;
        errors.kpiValue = true;
        setErrorsFormSendMessage(errors);
        setTypeAlertSendMessage('error');
        setAlertMessageSendMessage(
          t('send message.select a criterion and criterion value')
        );
        setShowAlertSendMessage(true);
        return false;
      }

      if (!hasKpi) {
        errors.kpi = true;
        setErrorsFormSendMessage(errors);
        setTypeAlertSendMessage('error');
        setAlertMessageSendMessage(t('send message.select a criterion')); // Alerta específico para KPI
        setShowAlertSendMessage(true);
        return false;
      }

      if (!hasKpiValue) {
        errors.kpiValue = true;
        setErrorsFormSendMessage(errors);
        setTypeAlertSendMessage('error');
        setAlertMessageSendMessage(t('send message.select a criterion value')); // Alerta específico para KPI Value
        setShowAlertSendMessage(true);
        return false;
      }
    }

    setErrorsFormSendMessage(initialState.errorsFormSendMessage);
    setTypeAlertSendMessage(initialState.typeAlertSendMessage);
    setShowAlertSendMessage(initialState.showAlertSendMessage);
    return true;
  };

  return {
    isLoadingSendMessage,
    areas,
    employees,
    kpis,
    anchorEl,
    openSendMessage,
    handleOpenSendMessage,
    handleCloseSendMessage,
    handleSendMessage,
    updateTypeSend,
    updateFormSendMessageData,
    formSendMessageData,
    handleOpenConfirmSendMessage,
    handleCloseConfirmSendMessage,
    openConfirmSendMessage,
    anchorElConfirmSendMessage,
    errorsFormSendMessage,
    showAlertSendMessage,
    alertMessageSendMessage,
    typeAlertSendMessage,
    handleCloseAlertSendMessage,
  };
};
